import { safeFetch } from '../helpers'
import { dtrinityRebateRewardFetcher } from './dtrinity'
import { llamaLendGaugeRewardFetcher } from './llamalend'
import {
  aaveMerklRewardFetcher,
  eulerMerklRewardFetcher,
  morphoCollateralMerklRewardFetcher,
} from './merkl'
import {
  ReserveTokenRewardResult,
  RewardFetchOutput,
  RewardFetcher,
  RewardResult,
} from './types'

/** Merge multiple RewardResults into a single map (mutates `target`) */
export function mergeRewardResults(target: RewardResult, source: RewardResult) {
  for (const [chainId, lenders] of Object.entries(source)) {
    if (!target[chainId]) target[chainId] = {}
    for (const [lender, assets] of Object.entries(lenders)) {
      if (!target[chainId][lender]) target[chainId][lender] = {}
      Object.assign(target[chainId][lender], assets)
    }
  }
}

/**
 * All registered reward fetchers.
 *
 * Two are NOT Merkl campaigns but plain on-chain reads: dTRINITY's borrow
 * rebate (an Aave incentive on the debt token) and LlamaLend's Curve gauges
 * (CRV emissions plus streamed extra tokens). Both live here rather than in
 * their lender's public-data fetch — a reward is a separate concern from a
 * market's rate, and folding these reads into the lender fetch would grow the
 * hot path for data most callers never ask for.
 */
const rewardFetchers: RewardFetcher[] = [
  aaveMerklRewardFetcher,
  eulerMerklRewardFetcher,
  morphoCollateralMerklRewardFetcher,
  dtrinityRebateRewardFetcher,
  llamaLendGaugeRewardFetcher,
]

/** Merge reserve-token-keyed results into a single map (mutates `target`) */
export function mergeReserveTokenRewards(
  target: ReserveTokenRewardResult,
  source: ReserveTokenRewardResult,
) {
  for (const [chainId, byToken] of Object.entries(source)) {
    if (!target[chainId]) target[chainId] = {}
    Object.assign(target[chainId], byToken)
  }
}

/**
 * Fetch and merge all lender reward sources.
 * Can be called standalone (for testing) or as a promise in a parallel flow.
 *
 * Two maps come back because two attributions are possible. Most protocols
 * identify their market from the campaign alone (Euler's EVault, Morpho's
 * market key) and land in `byLender`. Aave's campaigns don't name their
 * deployment, so they land in `byReserveToken` and the consumer joins them
 * against each market's own aToken/vToken — see RewardsByReserveToken.
 */
export async function fetchLenderRewards(): Promise<
  Required<RewardFetchOutput>
> {
  const results = await Promise.all(
    rewardFetchers.map((f) => safeFetch(f.label, f.fetch)),
  )

  const byLender: RewardResult = {}
  const byReserveToken: ReserveTokenRewardResult = {}
  for (const rr of results) {
    if (!rr) continue
    if (rr.byLender) mergeRewardResults(byLender, rr.byLender)
    if (rr.byReserveToken)
      mergeReserveTokenRewards(byReserveToken, rr.byReserveToken)
  }
  return { byLender, byReserveToken }
}
